import {Plural, Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ServerModel} from '../../../models/api/serverModel';
import {SettingsResourcePagesEnum} from '../../../models/url/settingsUrlModel';
import {AsyncMoreStatusesEnum} from '../../../store/appTypes';
import {ResourceList} from '../commons/resourceList';
import {serversTableColumns} from './serversTableConfig';

/*
 * Props.
 */

interface ServersListProps {
  servers: ReadonlyArray<ServerModel>;
  onLoadMore: Handler;
  moreStatus: AsyncMoreStatusesEnum;
  hasError: boolean;

  hasLocalApplication: boolean;
  // ID can be a number (local application server) or string (installed application server)
  onDeleteServers: HandlerOf<ReadonlyArray<number | string>>;

  infoBox: ReactNode | null;

  searchQuery: string;
  resetSearchQuery: Handler;
  onSearchQueryChange: HandlerOf<string>;

  // ID can be a number (local application server) or string (installed application server)
  navigateTo: (resourcePage: SettingsResourcePagesEnum, id?: number | string) => void;
}

/*
 * Component.
 */

export const ServersList: FC<ServersListProps> = (props) => {
  const {
    servers,
    onLoadMore,
    moreStatus,
    hasError,
    onDeleteServers,
    infoBox,
    searchQuery,
    resetSearchQuery,
    onSearchQueryChange,
    navigateTo,
    hasLocalApplication,
  } = props;

  return (
    <ResourceList
      tableColumns={serversTableColumns}
      items={servers}
      onLoadMore={onLoadMore}
      moreStatus={moreStatus}
      hasError={hasError}
      renderTableEmptyText={renderTableEmptyText}
      createLabel={hasLocalApplication ? <Trans id="servers-add">Add server</Trans> : undefined}
      onDeleteItems={hasLocalApplication ? onDeleteServers : undefined}
      infoBox={infoBox}
      searchQuery={searchQuery}
      resetSearchQuery={resetSearchQuery}
      onSearchQueryChange={onSearchQueryChange}
      navigateTo={navigateTo}
      renderSelectedItemsLabel={(count) => (
        <Plural
          id="servers-table-total-selected"
          value={count}
          one="1 server selected"
          other="# servers selected"
        />
      )}
      renderDeleteItemsTitle={(count, item) => (
        <Plural
          id="delete-servers-confirmation-dialog-title"
          value={count}
          one={`Delete server for ${item?.origin}`}
          other="Delete # servers"
        />
      )}
      renderDeleteItemsDescription={(count) => (
        <Plural
          id="delete-servers-confirmation-dialog-description"
          value={count}
          one="Permanently delete this server? This action cannot be undone."
          other="Permanently delete these servers? This action cannot be undone."
        />
      )}
    />
  );
};

/*
 * Helpers.
 */

function renderTableEmptyText(hasError: boolean, hasSearchQuery: boolean) {
  if (hasError) {
    return <Trans id="servers-table-error">An error occurred, please try again.</Trans>;
  }

  if (hasSearchQuery) {
    return <Trans id="servers-table-no-match">No server is matching your query.</Trans>;
  }

  return <Trans id="servers-table-empty-state">This application does not have any servers.</Trans>;
}
